import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { LeadTypeBadge } from "@/components/Badges";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OPP_LEAD_STATUSES, STATUS_META } from "@/lib/constants";

const blank = { name: "", phone: "", project: "", budget: "", visit_date: "", remarks: "", visit_done: false, lead_status: "" };

/** Create (pass `lead`) or edit (pass `opp`) an opportunity. */
export default function OpportunityDialog({ open, onOpenChange, lead, opp }) {
  const qc = useQueryClient();
  const [f, setF] = useState(blank);
  const editing = Boolean(opp);

  useEffect(() => {
    if (!open) return;
    const src = opp || lead || {};
    setF({
      name: src.name || "",
      phone: src.phone || "",
      project: src.project || "",
      budget: src.budget || "",
      visit_date: opp?.visit_date || "",
      remarks: opp?.remarks || "",
      visit_done: Boolean(opp?.visit_done),
      lead_status: opp?.lead_status || (OPP_LEAD_STATUSES.includes(lead?.status) ? lead.status : ""),
    });
  }, [open, opp, lead]);

  const projectsQ = useQuery({
    queryKey: ["properties", "names"],
    queryFn: async () => (await api.get("/properties", { params: { limit: 500 } })).data.items,
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const mut = useMutation({
    mutationFn: async () => {
      const body = { ...f, visit_date: f.visit_date || null, lead_status: f.lead_status || null };
      if (editing) return (await api.patch(`/opportunities/${opp.id}`, body)).data;
      return (await api.post("/opportunities", { ...body, lead_id: lead.id })).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Opportunity updated" : "Opportunity created");
      ["leads", "lead", "opportunities", "tasks", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save opportunity"),
  });

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const type = opp?.lead_type || lead?.lead_type || "bulk";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="opportunity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-navy">
            {editing ? "Edit Opportunity" : "Create Opportunity"} <LeadTypeBadge type={type} />
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name"><Input data-testid="opp-name-input" value={f.name} onChange={set("name")} /></Field>
          <Field label="Phone"><Input data-testid="opp-phone-input" value={f.phone} onChange={set("phone")} /></Field>
          <Field label="Project">
            <Input data-testid="opp-project-input" list="opp-project-list" value={f.project} onChange={set("project")} placeholder="Pick or type a project" />
            <datalist id="opp-project-list">
              {(projectsQ.data || []).map((p) => <option key={p.id} value={p.project_name} />)}
            </datalist>
          </Field>
          <Field label="Budget"><Input data-testid="opp-budget-input" value={f.budget} onChange={set("budget")} placeholder="e.g. 1Cr - 1.5Cr" /></Field>
          <Field label="Lead Status" className="sm:col-span-2">
            <Select value={f.lead_status} onValueChange={set("lead_status")}>
              <SelectTrigger data-testid="opp-lead-status-select"><SelectValue placeholder="Choose lead status" /></SelectTrigger>
              <SelectContent>
                {OPP_LEAD_STATUSES.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`opp-lead-status-${k}`}>{STATUS_META[k].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Visit date & time" className="sm:col-span-2">
            <Input data-testid="opp-visit-date-input" type="datetime-local" value={f.visit_date} onChange={set("visit_date")} />
          </Field>
          <Field label="Remarks" className="sm:col-span-2">
            <Textarea data-testid="opp-remarks-input" rows={3} value={f.remarks} onChange={set("remarks")} />
          </Field>
          <label className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 sm:col-span-2">
            <span className="text-sm font-medium text-slate-700">Visit Done</span>
            <Switch data-testid="opp-visit-done-switch" checked={f.visit_done} onCheckedChange={set("visit_done")} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="opp-cancel-btn">Cancel</Button>
          <Button
            data-testid="opp-save-btn"
            className="bg-navy hover:brightness-110"
            disabled={!f.name.trim() || !f.phone.trim() || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending ? "Saving…" : editing ? "Save changes" : "Create Opportunity"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="mb-1 block text-xs font-medium text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
